import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { authService } from '../../services';
import { setTokens, setUserData } from '../../utils/auth';
import { hasValidEndpoints, storeEndpoints } from '../../utils/endpointStorage';
import routes from '../../utils/routes';
import './Login.css';

// Function to fetch and store endpoints
const fetchEndpoints = async () => {
  if (hasValidEndpoints()) {
    console.log('Using cached endpoints (less than 3 hours old)');
    return;
  }
  
  console.log('Fetching fresh endpoints from server');
  try {
    const response = await authService.getEndpoints();
    console.log('Endpoints response:', response.data);
    
    // Extract the websocket URL from the response based on the observed structure
    // The structure is: { success, timestamp, data: { message: [websocketUrl], success, timestamp } }
    let endpoints = null;
    
    if (response.data && response.data.data && response.data.data.message && Array.isArray(response.data.data.message)) {
      // Extract the websocket URL from the message array
      const websocketUrl = response.data.data.message[0];
      console.log('Found websocket URL in response:', websocketUrl);
      
      // Store the URL in a structured format
      endpoints = {
        websocket: websocketUrl,
        timestamp: response.data.timestamp || new Date().toISOString(),
        fetchedAt: new Date().toISOString(),
      };
    } else if (response.data && response.data.message && Array.isArray(response.data.message)) {
      // Alternative structure if data is not nested
      const websocketUrl = response.data.message[0];
      console.log('Found websocket URL in response (alternative structure):', websocketUrl);
      
      endpoints = {
        websocket: websocketUrl,
        timestamp: response.data.timestamp || new Date().toISOString(),
        fetchedAt: new Date().toISOString(),
      };
    } else {
      // Fallback - store the whole response for debugging
      console.warn('Could not find websocket URL in expected structure, storing full response');
      endpoints = response.data;
    }
    
    // Store the endpoints with current timestamp
    storeEndpoints(endpoints);
    
  } catch (error) {
    console.error('Error fetching endpoints:', error);
    if (error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error data:', error.response.data);
    }
  }
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  // On component mount, clear any authentication-related flags to prevent redirect loops
  useEffect(() => {
    // Clear any auth redirection flags
    localStorage.removeItem('auth_redirect_triggered');
    
    // Check for stale tokens - if we got redirected to login due to auth failure
    // but the token is still there, we need to clean it up
    if (location.state?.errorType === 'session_expired' && localStorage.getItem('token')) {
      console.log('Found stale token after session expiry, clearing it');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
    }
  }, [location.state]);

  // Check for errors passed from Dashboard component
  useEffect(() => {
    if (location.state?.errorType) {
      const errorType = location.state.errorType;
      setServerError(location.state.errorMessage || 'An error occurred. Please try again.');
      
      // Set error type in state for styling purposes
      navigate(location.pathname, { replace: true, state: { errorType } });
    }
  }, [location, navigate]);

  // Form validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required'),
  });

  // Formik form handling
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setServerError('');
        
        // Call login API with our new authService
        console.log('Attempting to sign in with email:', values.email);
        
        try {
          const response = await authService.signin(values.email, values.password);
          console.log('Sign in response:', response.data);
          
          // Extract data from response
          const responseData = response.data;
          
          // Check for different token formats
          // Format 1: Separate accessToken and refreshToken fields
          const hasStandardTokens = responseData.accessToken && responseData.refreshToken;
          
          // Format 2: Single token field (checking all possible locations including deep nesting)
          const hasSingleToken = responseData.token || 
                              (responseData.data && responseData.data.token) || 
                              (responseData.data && responseData.data.data && responseData.data.data.token);
          
          // Log token detection for debugging
          console.log('Token detection:', {
            hasStandardTokens,
            hasSingleToken,
            hasDeepToken: responseData.data && responseData.data.data && responseData.data.data.token,
            responseStructure: Object.keys(responseData),
            dataKeys: responseData.data ? Object.keys(responseData.data) : 'no data object'
          });
          
          // If we have any type of token, proceed with login
          if (hasStandardTokens || hasSingleToken) {
            console.log('Login successful (tokens found), redirecting to dashboard');
            
            // Handle different token formats
            if (hasStandardTokens) {
              // Standard format with accessToken and refreshToken
              setTokens(responseData.accessToken, responseData.refreshToken);
              setUserData(responseData.user || { email: values.email });
            } else if (hasSingleToken) {
              // Single token format - find the token at whatever level it exists
              let token;
              if (responseData.token) {
                token = responseData.token;
              } else if (responseData.data && responseData.data.token) {
                token = responseData.data.token;
              } else if (responseData.data && responseData.data.data && responseData.data.data.token) {
                token = responseData.data.data.token;
              }
              
              console.log('Found token, using for authentication');
              // Use the same token for both access and refresh since we only have one
              setTokens(token, token);
              setUserData({ email: values.email });
              console.log('Using single token format for authentication');
            }
            
            // Fetch endpoints if needed after successful login
            await fetchEndpoints();
            
            // Redirect to dashboard
            navigate(routes.protected.DASHBOARD);
            return; // Exit early to prevent other checks
          } 
          
          // SECOND CHECK: Look for explicit OTP requirements
          if (responseData.requireOTP === true) {
            console.log('OTP verification explicitly required (requireOTP flag)');
            // Redirect to OTP verification page
            navigate(routes.public.VERIFY_OTP, { 
              state: { 
                email: values.email,
                isLogin: true
              } 
            });
            return; // Exit early
          }
          
          // THIRD CHECK: Check for verification messages only if no tokens were found
          const hasVerificationMessage = 
            responseData.message && 
            (responseData.message.includes('Verification code sent') || 
             responseData.message.includes('check your inbox') ||
             responseData.message.includes('verification'));
             
          if (hasVerificationMessage && !responseData.accessToken) {
            console.log('OTP verification required based on message:', responseData.message);
            // Redirect to OTP verification page
            navigate(routes.public.VERIFY_OTP, { 
              state: { 
                email: values.email,
                isLogin: true
              } 
            });
            return; // Exit early
          }
          
          // FOURTH CHECK: If success is true but no tokens or clear messages, check data structure carefully
          if (responseData.success === true && !responseData.accessToken) {
            // Check if there are any indicators this might be a verification response
            const mightNeedVerification = !responseData.user || Object.keys(responseData).length <= 3;
            
            if (mightNeedVerification) {
              console.log('Potential verification needed based on response structure');
              // Show confirmation to user
              setServerError('Verification may be required. Check your email for a verification code.');
              // Add a button to go to OTP page if needed
              return;
            }
          }
          
          // If we got here, response format is unexpected
          console.warn('Unexpected login response format:', responseData);
          setServerError('Login successful but response format is unexpected. Please try again or contact support.');
        } catch (apiError) {
          console.error('API Error during login:', apiError);
          
          if (apiError.response) {
            console.error('Response data:', apiError.response.data);
            console.error('Response status:', apiError.response.status);
            
            const errorData = apiError.response.data;
            
            // Check for different token formats in error response
            // Format 1: Separate accessToken and refreshToken fields
            const hasStandardTokens = errorData.accessToken && errorData.refreshToken;
            
            // Format 2: Single token field - check all possible locations
            const hasSingleToken = errorData.token || 
                               (errorData.data && errorData.data.token) ||
                               (errorData.data && errorData.data.data && errorData.data.data.token);
            
            console.log('Error token detection:', {
              hasStandardTokens,
              hasSingleToken,
              hasDeepToken: errorData.data && errorData.data.data && errorData.data.data.token,
              errorStructure: Object.keys(errorData),
              dataKeys: errorData.data ? Object.keys(errorData.data) : 'no data object'
            });
            
            // If any token is found in error response, proceed with login
            if (hasStandardTokens || hasSingleToken) {
              console.log('Found tokens in error response, proceeding with login');
              
              if (hasStandardTokens) {
                setTokens(errorData.accessToken, errorData.refreshToken);
                setUserData(errorData.user || { email: values.email });
              } else if (hasSingleToken) {
                // Find the token at whatever level it exists
                let token;
                if (errorData.token) {
                  token = errorData.token;
                } else if (errorData.data && errorData.data.token) {
                  token = errorData.data.token;
                } else if (errorData.data && errorData.data.data && errorData.data.data.token) {
                  token = errorData.data.data.token;
                }
                
                setTokens(token, token);
                setUserData({ email: values.email });
                console.log('Using single token format from error response');
              }
              
              navigate(routes.protected.DASHBOARD);
              return;
            }
            
            // SECOND: Check for verification messages
            if (errorData.message) {
              // Check for verification code messages
              if (errorData.message.includes('Verification code sent') || 
                  errorData.message.includes('check your inbox') ||
                  errorData.message.includes('verification code')) {
                
                console.log('Verification code message in error response');
                navigate(routes.public.VERIFY_OTP, { 
                  state: { 
                    email: values.email,
                    isLogin: true
                  } 
                });
                return;
              }
            }
            
            // If it's just a regular error, show the message
            const errorMessage = errorData.message || 
                              errorData.error ||
                              'An error occurred during login. Please try again.';
            setServerError(errorMessage);
          } else {
            setServerError('An error occurred during login. Please try again.');
          }
        }
      } catch (error) {
        console.error('Unexpected login error:', error);
        setServerError('An unexpected error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className="auth-page-container">
      <div className="auth-page-card">
        <div className="auth-page-header">
          <div className="auth-page-title">Welcome</div>
          <div className="auth-page-subtitle">Sign in to your WDTP account</div>
        </div>

        {serverError && (
          <div 
            className={
              location.state?.errorType === 'rate_limit' 
                ? "rate-limit-error" 
                : location.state?.errorType === 'permission_denied'
                  ? "permission-denied-error"
                  : "auth-page-error"
            } 
            style={{ marginBottom: 'var(--spacing-md)' }}
          >
            {serverError}
          </div>
        )}

        <form className="auth-page-form" onSubmit={formik.handleSubmit}>
          <Input
            name="email"
            type="email"
            label="Email Address"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && formik.errors.email}
            placeholder="Enter your email"
            required
          />

          <Input
            name="password"
            type="password"
            label="Password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && formik.errors.password}
            placeholder="Enter your password"
            required
          />

          <div className="login-forgot-password">
            <Link to={routes.public.FORGOT_PASSWORD}>Forgot password?</Link>
          </div>

          <Button
            type="submit"
            variant="primary"
            className="auth-page-submit-button"
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        <div className="auth-page-divider">
          <span className="auth-page-divider-text">Or</span>
        </div>

        <div className="auth-page-link-text">
          Don't have an account? <Link to={routes.public.REGISTER}>Sign Up</Link>
        </div>
      </div>
    </div>
  );
};

export default Login; 